// @ts-check
import axios from 'axios';
import {
  GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
  GET_PROJECT_BINDINGS,
  GET_USER_ROLES,
  GET_USERS,
  LIST_ENTITY_CONFIGS_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { integration_options, is_number, response_rows, to_array } from './tenant_configuration_editor_normalisers';
import { error_message } from './tenant_configuration_editor_normalisers';
import {
  is_domain_option,
  is_project_binding,
  mapped_domain_option,
  normalize_project_binding,
  selected_domain_keys,
  selected_project,
  sector_from_description
} from './tenant_configuration_editor_project_helpers';
import {
  is_role_option,
  normalize_project_assignment,
  normalize_role_option,
  normalize_user_option,
  selected_tenant_contact
} from './tenant_configuration_editor_role_helpers';
import { tenant_configuration_editor_save_methods } from './tenant_configuration_editor_save_methods';

/**
 * @typedef {import('./tenant_configuration_editor_types').TenantConfigurationEditorState} TenantConfigurationEditorState
 * @typedef {import('./tenant_configuration_editor_types').TenantConfigurationEditorInstance} TenantConfigurationEditorInstance
 * @typedef {import('./tenant_configuration_editor_types').SelectedTenantPayload} SelectedTenantPayload
 * @typedef {import('./tenant_configuration_editor_types').NormalizedSelectedTenant} NormalizedSelectedTenant
 * @typedef {import('./tenant_configuration_editor_types').UserOption} UserOption
 */

/** @returns {TenantConfigurationEditorState} */
export function create_editor_state() {
  const state = Object.fromEntries([]);
  state.step = 1;
  state.loading = false;
  state.saving = false;
  state.selected_project_id = null;
  state.selected_project = null;
  state.contact_user_id = null;
  state.contact_role_id = null;
  state.domain_options = [];
  state.role_options = [];
  state.project_bindings = [];
  state.form = {
    organisation_name: '',
    sector: '',
    primary_contact: '',
    primary_email: '',
    primary_phone: '',
    domains: [],
    roles: [],
    integrations: []
  };
  state.integration_options = integration_options();
  return state;
}

/** @param {TenantConfigurationEditorInstance} editor @returns {void} */
export function reset_editor(editor) {
  const state = create_editor_state();
  editor.step = state.step;
  editor.selected_project_id = state.selected_project_id;
  editor.selected_project = state.selected_project;
  editor.contact_user_id = state.contact_user_id;
  editor.contact_role_id = state.contact_role_id;
  editor.domain_options = state.domain_options;
  editor.role_options = state.role_options;
  editor.project_bindings = state.project_bindings;
  editor.form = state.form;
}

/** @param {TenantConfigurationEditorInstance} editor @returns {void} */
export function emit_close_when_idle(editor) {
  if (!editor.saving) editor.$emit('close');
}

/** @param {TenantConfigurationEditorInstance} editor @param {boolean} loading @returns {void} */
export function set_editor_loading(editor, loading) {
  editor.loading = loading;
}

/** @param {TenantConfigurationEditorInstance} editor @returns {number|null} */
export function resolve_selected_tenant_id(editor) {
  const project_id = Number(editor.selected_project_id);
  return Number.isFinite(project_id) && project_id > 0 ? project_id : null;
}

/**
 * @param {TenantConfigurationEditorInstance} editor
 * @param {number} project_id
 * @returns {Promise<SelectedTenantPayload>}
 */
export async function fetch_selected_tenant(editor, project_id) {
  const [users_response, roles_response, configs_response, bindings_response] = await Promise.all([
    axios.get(GET_USERS, get_request_config({ params: { project_id, page: 1, page_size: 100 } })),
    axios.get(GET_USER_ROLES, get_request_config()),
    axios.get(LIST_ENTITY_CONFIGS_URL, get_request_config({ params: { is_active: true } })),
    axios.get(GET_PROJECT_BINDINGS, get_request_config({ params: { project_id } }))
  ]);

  const tenant = Object.fromEntries([]);
  tenant.project = selected_project(editor.normalized_projects, project_id);
  tenant.users = to_array(response_rows(users_response), normalize_user_option);
  tenant.roles = to_array(response_rows(roles_response), normalize_role_option).filter(is_role_option);
  tenant.domains = to_array(response_rows(configs_response), mapped_domain_option).filter(is_domain_option);
  tenant.bindings = to_array(response_rows(bindings_response), normalize_project_binding).filter(is_project_binding);
  return tenant;
}

/** @param {SelectedTenantPayload} payload @returns {NormalizedSelectedTenant} */
export function normalise_selected_tenant(payload) {
  const tenant = Object.fromEntries([]);
  tenant.project = payload.project;
  tenant.contact = selected_tenant_contact(payload.project, payload.users);
  tenant.roles = payload.roles;
  tenant.domains = payload.domains;
  tenant.bindings = payload.bindings;
  return tenant;
}

/** @param {TenantConfigurationEditorInstance} editor @param {UserOption|null} contact @returns {void} */
export function apply_contact_state(editor, contact) {
  if (!contact) return;

  editor.contact_user_id = contact.id;
  editor.contact_role_id = contact.role_id;
  editor.form.primary_contact = `${contact.first_name} ${contact.last_name}`.trim();
  editor.form.primary_email = contact.email;
  editor.form.primary_phone = contact.phone;
}

/** @param {TenantConfigurationEditorInstance} editor @param {NormalizedSelectedTenant} tenant @returns {void} */
export function apply_selected_tenant_state(editor, tenant) {
  editor.selected_project = tenant.project;
  editor.form.organisation_name = tenant.project?.name || tenant.project?.project_name || '';
  editor.form.sector = editor.sector_from_description(tenant.project?.project_description);
  editor.role_options = tenant.roles;
  editor.domain_options = tenant.domains;
  editor.project_bindings = tenant.bindings;
  editor.form.domains = selected_domain_keys(tenant.bindings, tenant.domains);
  apply_contact_state(editor, tenant.contact);
}

/** @param {TenantConfigurationEditorInstance} editor @param {number} project_id @returns {Promise<void>} */
export async function load_selected_tenant_roles(editor, project_id) {
  const role_assignments = await Promise.all(editor.role_options.map(async (role) => {
    const response = await axios.post(
      GET_ASSIGNED_PROJECTS_FOR_ROLE_URL,
      { role_id: role.key },
      get_request_config()
    );
    const assignments = to_array(response_rows(response), normalize_project_assignment);
    return assignments.some((assignment) => assignment.project === project_id) ? role.key : null;
  }));

  editor.form.roles = role_assignments.filter(is_number);
}

/** @this {TenantConfigurationEditorInstance} @returns {void} */
function reset() {
  reset_editor(this);
}

/** @this {TenantConfigurationEditorInstance} @returns {void} */
function close() {
  emit_close_when_idle(this);
}

/** @param {unknown} description @returns {string} */
function sector_method(description) {
  return sector_from_description(description);
}

/** @param {unknown} profile @returns {UserOption} */
function user_record(profile = {}) {
  return normalize_user_option(profile);
}

/** @this {TenantConfigurationEditorInstance} @returns {Promise<void>} */
async function load_selected_tenant() {
  const project_id = resolve_selected_tenant_id(this);
  if (!project_id) return;

  set_editor_loading(this, true);
  try {
    const tenant = normalise_selected_tenant(await fetch_selected_tenant(this, project_id));
    apply_selected_tenant_state(this, tenant);
    await load_selected_tenant_roles(this, project_id);
  } finally {
    set_editor_loading(this, false);
  }
}

/** @this {TenantConfigurationEditorInstance} @returns {Promise<void>} */
async function continue_step() {
  if (!this.selected_project_id) {
    this.show_error('Select a tenant to continue.');
    return;
  }
  if (this.step === 2 && !this.form.domains.length) {
    this.show_error('Select at least one case domain.');
    return;
  }
  if (this.step === 3 && !this.form.roles.length) {
    this.show_error('Select at least one role.');
    return;
  }

  this.saving = true;
  try {
    if (this.step === 1 && !await this.save_details()) return;
    if (this.step === 2) await this.save_domains();
    if (this.step === 3) await this.save_roles();
    this.step += 1;
  } catch (error) {
    this.show_error(error_message(error));
  } finally {
    this.saving = false;
  }
}

/** @this {TenantConfigurationEditorInstance} @returns {void} */
function finish() {
  this.$emit('saved');
  this.$emit('close');
}

export const tenant_configuration_editor_methods = {
  ...tenant_configuration_editor_save_methods,
  reset,
  close,
  sector_from_description: sector_method,
  user_record,
  load_selected_tenant,
  continue_step,
  finish
};
